// src/pages/StatsPage.js
//
// Admin istatistikleri (/v2/admin/stats/...). Dört uç, iki sekme:
//   Genel   — overview + timeseries + operations
//   Katalog sağlığı — yavaş uç, kendi sekmesinde ve tembel
//
// Kılavuzun tasarımı belirleyen iki kuralı burada uygulanıyor:
//   1) Ciro ASLA tek sayıda toplanmaz. PayPal tutarı gerçek tahsilat (EUR),
//      mağaza cirosu tahmindir (TRY) — farklı para birimi, farklı güvenilirlik.
//   2) Ölçülmeyen şey sayı olarak gösterilmez: `activationDataAvailable=false`
//      iken "0 aktivasyon" gerçek bir ölçüm değildir, o blok yazıya döner.
//
// Hacim düşük (14 ayda ~192 sipariş): büyük sayı animasyonu, sparkline ve
// "geçen döneme göre %X" rozetleri bilerek yok — bu veride gürültü yaratırlar.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './StatsPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import OperationsPanel from '../components/stats/OperationsPanel';
import CatalogHealthPanel from '../components/stats/CatalogHealthPanel';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCartShopping,
    faCircleCheck,
    faClock,
    faTriangleExclamation,
    faCreditCard,
    faStore,
    faUsers,
    faSimCard,
} from '@fortawesome/free-solid-svg-icons';
import {
    RANGE_PRESETS,
    DEFAULT_RANGE_PRESET,
    BUCKETS,
    fetchOverview,
    fetchTimeseries,
    readStatsError,
    formatCount,
    formatTry,
    formatEur,
    formatPercent,
    formatDateTime,
    bucketLabel,
} from '../services/statsApi';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

// Platform halkası ve sıralama çubukları aynı paleti kullanır.
const PLATFORM_COLORS = ['#4b34e8', '#12a594', '#6a54ff', '#d08700', '#8b89a0'];

// Ham platform anahtarlarının düzgün yazımı ("Paypal"/"Ios" olmasın).
const PLATFORM_LABELS = { paypal: 'PayPal', ios: 'iOS', android: 'Android', manual: 'Manuel', web: 'Web' };
const platformLabel = (key) => PLATFORM_LABELS[key] || key;

const TABS = [
    { value: 'overview', label: 'Genel' },
    { value: 'catalog', label: 'Katalog sağlığı' },
];

const buildPresetRange = (value) =>
    (RANGE_PRESETS.find((p) => p.value === value) || RANGE_PRESETS[1]).build();

/** Tek KPI kartı: ikon + etiket + büyük rakam + açıklama. */
const Kpi = ({ icon, tone = 'signal', label, labelExtra, value, hint }) => (
    <div className={`${styles.kpi} ${styles[`kpi_${tone}`]}`}>
        <span className={styles.kpiIcon} aria-hidden="true">
            <FontAwesomeIcon icon={icon} />
        </span>
        <span className={styles.kpiLabel}>
            {label}
            {labelExtra}
        </span>
        <span className={`${styles.kpiValue} num`}>{value}</span>
        {hint && <span className={styles.kpiHint}>{hint}</span>}
    </div>
);

/**
 * Huni basamağı. Basamaklar arasındaki geçiş oranı da yazılır: "118'den 80'e"
 * bilgisini okuyucunun kafadan hesaplaması gerekmesin.
 */
const FunnelStep = ({ label, count, amount, max, prevCount }) => {
    const width = max > 0 ? Math.max(3, Math.round((count / max) * 100)) : 0;
    const drop = prevCount > 0 ? Math.round((count / prevCount) * 100) : null;
    return (
        <div className={styles.funnelStep}>
            {drop !== null && (
                <div className={styles.funnelDrop}>
                    <span className="num">%{drop}</span> geçti
                </div>
            )}
            <div className={styles.funnelHead}>
                <span className={styles.funnelLabel}>{label}</span>
                <span className={`${styles.funnelCount} num`}>
                    {formatCount(count)}
                    {amount ? <span className={styles.funnelAmount}> · {formatEur(amount)}</span> : null}
                </span>
            </div>
            <div className={styles.funnelTrack}>
                <div className={styles.funnelBar} style={{ width: `${width}%` }} />
            </div>
        </div>
    );
};

/** Sıralama listesi satırı: sıra numarası + ad + pay çubuğu + değer. */
const RankRow = ({ rank, title, subtitle, value, share }) => (
    <li className={styles.rankRow}>
        <span className={styles.rankNo}>{rank}</span>
        <span className={styles.rankBody}>
            <span className={styles.rankHead}>
                <span className={styles.rankTitle}>{title}</span>
                <span className={`${styles.rankValue} num`}>{value}</span>
            </span>
            <span className={styles.rankTrack}>
                <span className={styles.rankBar} style={{ width: `${Math.max(3, share)}%` }} />
            </span>
            {subtitle && <span className={styles.rankSub}>{subtitle}</span>}
        </span>
    </li>
);

const StatsPage = () => {
    const [preset, setPreset] = useState(DEFAULT_RANGE_PRESET);
    const [range, setRange] = useState(() => buildPresetRange(DEFAULT_RANGE_PRESET));
    const [bucket, setBucket] = useState('month');
    const [tab, setTab] = useState('overview');
    // Katalog sağlığı yavaş uç: sekme ilk açılana kadar hiç monte edilmez.
    const [catalogVisited, setCatalogVisited] = useState(false);

    const [overview, setOverview] = useState(null);
    const [series, setSeries] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [overviewData, seriesData] = await Promise.all([
                fetchOverview(range),
                fetchTimeseries({ ...range, bucket }),
            ]);
            setOverview(overviewData);
            setSeries(seriesData);
        } catch (err) {
            console.error('İstatistikler alınamadı:', err);
            setError(readStatsError(err, 'İstatistikler alınamadı.').message);
            setOverview(null);
            setSeries(null);
        } finally {
            setIsLoading(false);
        }
    }, [range, bucket]);

    useEffect(() => { load(); }, [load]);

    const handlePreset = (value) => {
        setPreset(value);
        setRange(buildPresetRange(value));
    };

    const handleDateChange = (field) => (e) => {
        setPreset('custom');
        setRange((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // Boş kovalar sunucuda sıfırla dolduruluyor; ara doldurma yapmıyoruz.
    const chart = useMemo(() => {
        const points = series?.points || [];
        return {
            labels: points.map((p) => bucketLabel(p.date, series?.bucket)),
            datasets: [
                {
                    label: 'Sipariş',
                    data: points.map((p) => p.orders),
                    backgroundColor: '#dcd8fd',
                    borderRadius: 6,
                    maxBarThickness: 34,
                },
                {
                    label: 'Başarılı',
                    data: points.map((p) => p.successful),
                    backgroundColor: '#4b34e8',
                    borderRadius: 6,
                    maxBarThickness: 34,
                },
            ],
        };
    }, [series]);

    const platformChart = useMemo(() => {
        const list = overview?.platforms || [];
        return {
            labels: list.map((p) => platformLabel(p.platform)),
            datasets: [{
                data: list.map((p) => p.total),
                backgroundColor: list.map((_, i) => PLATFORM_COLORS[i % PLATFORM_COLORS.length]),
                borderWidth: 0,
                hoverOffset: 6,
            }],
        };
    }, [overview]);

    const donutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: { legend: { display: false } }, // kendi lejandımız sayıları da taşıyor
    }), []);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, padding: 16, color: '#55536a' },
            },
            tooltip: {
                backgroundColor: '#16151f',
                padding: 10,
                cornerRadius: 8,
                displayColors: true,
                callbacks: { title: (items) => items[0]?.label },
            },
        },
        scales: {
            x: { grid: { display: false }, border: { display: false }, ticks: { color: '#8b89a0' } },
            y: {
                beginAtZero: true,
                border: { display: false },
                ticks: { precision: 0, color: '#8b89a0', maxTicksLimit: 5 },
                grid: { color: '#eef0f6' },
            },
        },
    }), []);

    const orders = overview?.orders || {};
    const revenue = overview?.revenue || {};
    const users = overview?.users || {};
    const esims = overview?.esims || {};
    const paypalFunnel = overview?.funnel?.paypal;
    const storeFunnel = overview?.funnel?.store;
    const platforms = overview?.platforms || [];
    const topPlans = overview?.topPlans || [];
    const topCountries = overview?.topCountries || [];

    // Huni çubuklarının ölçeği: terk edilenler basamak değil sonuç olduğu için
    // ölçeğe dahil edilmez.
    const paypalMax = Math.max(
        ...(paypalFunnel?.steps || [])
            .filter((s) => s.key !== 'abandoned')
            .map((s) => Number(s.count) || 0),
        1,
    );
    const abandoned = (paypalFunnel?.steps || []).find((s) => s.key === 'abandoned');
    const storeMax = Math.max(...(storeFunnel?.steps || []).map((s) => Number(s.count) || 0), 1);
    const topPlanMax = Math.max(...topPlans.map((p) => Number(p.orders) || 0), 1);
    const topCountryMax = Math.max(...topCountries.map((c) => Number(c.orders) || 0), 1);

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>İstatistikler</h1>
                    {overview?.generatedAt && (
                        <p className={styles.subtitle}>{formatDateTime(overview.generatedAt)} itibarıyla</p>
                    )}
                </div>
                {/* Tarih aralığı ve yenileme yalnızca Genel sekmesine ait:
                    catalog-health ucu tarih parametresi almaz ve kendi
                    "Yeniden çalıştır" düğmesiyle tazelenir. */}
                {tab === 'overview' && (
                    <div className={styles.headerActions}>
                        <button type="button" className="ghost" onClick={load} disabled={isLoading}>
                            {isLoading ? 'Yükleniyor…' : 'Yenile'}
                        </button>
                    </div>
                )}
            </header>

            {/* `hidden` özniteliği burada işe yaramaz: .toolbar'ın display:flex
                kuralı tarayıcının [hidden]{display:none} kuralını ezer. */}
            {tab === 'overview' && (
            <div className={styles.toolbar}>
                <div className={styles.presets} role="group" aria-label="Tarih aralığı">
                    {RANGE_PRESETS.map((p) => (
                        <button
                            key={p.value}
                            type="button"
                            className={`${styles.presetButton} ${preset === p.value ? styles.presetActive : ''}`}
                            aria-pressed={preset === p.value}
                            onClick={() => handlePreset(p.value)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className={styles.dateFields}>
                    <label htmlFor="stats-from">Başlangıç</label>
                    <input id="stats-from" type="date" className={styles.dateInput} value={range.from} onChange={handleDateChange('from')} />
                    <label htmlFor="stats-to">Bitiş</label>
                    <input id="stats-to" type="date" className={styles.dateInput} value={range.to} onChange={handleDateChange('to')} />
                </div>
            </div>
            )}

            <div className={styles.tabs} role="tablist">
                {TABS.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.value}
                        className={`${styles.tab} ${tab === t.value ? styles.tabActive : ''}`}
                        onClick={() => {
                            setTab(t.value);
                            if (t.value === 'catalog') setCatalogVisited(true);
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'catalog' ? (
                catalogVisited ? <CatalogHealthPanel /> : null
            ) : isLoading && !overview ? (
                <div className={styles.loadingContainer}><LoadingSpinner /></div>
            ) : error ? (
                <div className={styles.errorPanel}>
                    <span className={styles.errorIcon} aria-hidden="true">⚠</span>
                    <p className={styles.errorMessage}>{error}</p>
                    <button type="button" className="ghost" onClick={load}>Tekrar dene</button>
                </div>
            ) : (
                <div className={styles.stack}>
                    {/* --- KPI --- */}
                    <div className={styles.kpiGrid}>
                        <Kpi
                            icon={faCartShopping}
                            tone="signal"
                            label="Sipariş"
                            value={formatCount(orders.total)}
                            hint={orders.previousPeriodTotal !== undefined && (
                                <>önceki dönem <span className="num">{formatCount(orders.previousPeriodTotal)}</span></>
                            )}
                        />
                        <Kpi
                            icon={faCircleCheck}
                            tone="ok"
                            label="Başarılı"
                            value={formatCount(orders.successful)}
                            hint={<>{formatPercent(orders.successRate)} başarı oranı</>}
                        />
                        <Kpi
                            icon={faClock}
                            tone="neutral"
                            label="Bekleyen"
                            value={formatCount(orders.pending)}
                            hint="ne başarılı ne hatalı"
                        />
                        <Kpi
                            icon={faTriangleExclamation}
                            tone={orders.failed > 0 ? 'danger' : 'neutral'}
                            label="Hatalı"
                            value={formatCount(orders.failed)}
                            hint={orders.failed > 0 ? 'inceleme gerekiyor' : 'hata yok'}
                        />
                    </div>

                    {/* Ciro tek sayıya toplanmaz: biri gerçek tahsilat, diğeri tahmin.
                        Bu ayrım renkle de görünür — gerçek olan dolu, tahmin olan taralı. */}
                    <div className={styles.kpiGrid}>
                        <Kpi
                            icon={faCreditCard}
                            tone="money"
                            label="PayPal tahsilat"
                            value={formatEur(revenue.paypalCompletedEur)}
                            hint={<><span className="num">{formatCount(revenue.paypalCompletedCount)}</span> işlem · gerçek tahsilat</>}
                        />
                        <Kpi
                            icon={faStore}
                            tone="estimate"
                            label="Mağaza cirosu"
                            labelExtra={<span className={styles.estimateTag} title={revenue.note || undefined}>tahmini</span>}
                            value={formatTry(revenue.storeEstimatedTry)}
                            hint={<><span className="num">{formatCount(revenue.storeOrderCount)}</span> sipariş · planın bugünkü fiyatıyla</>}
                        />
                        <Kpi
                            icon={faUsers}
                            tone="neutral"
                            label="Kullanıcı"
                            value={formatCount(users.total)}
                            hint={<>aralıkta <span className="num">+{formatCount(users.newInRange)}</span></>}
                        />
                        <Kpi
                            icon={faSimCard}
                            tone="neutral"
                            label="eSIM"
                            value={formatCount(esims.total)}
                            hint={
                                /* Aktivasyon alanı sağlayıcıdan güncellenmiyor; "0 aktivasyon"
                                   gerçek bir ölçüm olmadığı için sayı yerine not yazılır. */
                                esims.activationDataAvailable
                                    ? Object.entries(esims.byActivationStatus || {})
                                        .map(([status, count]) => `${status}: ${count}`)
                                        .join(' · ')
                                    : 'aktivasyon verisi henüz toplanmıyor'
                            }
                        />
                    </div>

                    {/* --- Huni + platform --- */}
                    <div className={styles.twoCol}>
                        <section className={styles.block}>
                            <header className={styles.blockHeader}>
                                <h2 className={styles.blockTitle}>Dönüşüm hunisi</h2>
                            </header>

                            {paypalFunnel && (
                                <div className={styles.funnel}>
                                    <div className={styles.funnelTitle}>
                                        PayPal
                                        <span className={styles.funnelRate}>{formatPercent(paypalFunnel.conversionRate)} dönüşüm</span>
                                    </div>
                                    {paypalFunnel.steps
                                        .filter((s) => s.key !== 'abandoned')
                                        .map((step, i, steps) => (
                                            <FunnelStep
                                                key={step.key}
                                                label={step.label}
                                                count={step.count}
                                                amount={step.amountEur}
                                                max={paypalMax}
                                                prevCount={i > 0 ? steps[i - 1].count : null}
                                            />
                                        ))}
                                    {/* Terk edilenler huninin basamağı değil sonucudur: altta, kayıp satırı olarak. */}
                                    {abandoned && (
                                        <div className={styles.lossRow}>
                                            <span>{abandoned.label}</span>
                                            <span className="num">
                                                {formatCount(abandoned.count)} · {formatEur(abandoned.amountEur)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {storeFunnel && (
                                <div className={styles.funnel}>
                                    <div className={styles.funnelTitle}>
                                        Mağaza
                                        <span className={styles.funnelRate}>{formatPercent(storeFunnel.conversionRate)} teslimat</span>
                                    </div>
                                    {storeFunnel.steps.map((step, i, steps) => (
                                        <FunnelStep
                                            key={step.key}
                                            label={step.label}
                                            count={step.count}
                                            max={storeMax}
                                            prevCount={i > 0 ? steps[i - 1].count : null}
                                        />
                                    ))}
                                    <p className={styles.blockNote}>
                                        Mağaza kanalında risk ödeme değil teslimattır: satın alma tamamlanmıştır,
                                        eksik olan eSIM'in ulaşmasıdır.
                                    </p>
                                </div>
                            )}
                        </section>

                        <section className={styles.block}>
                            <header className={styles.blockHeader}>
                                <h2 className={styles.blockTitle}>Platform dağılımı</h2>
                            </header>
                            <div className={styles.donutRow}>
                                <div className={styles.donutWrap}>
                                    <Doughnut data={platformChart} options={donutOptions} />
                                    {/* Ortada toplam YAZMIYORUZ: platform satırlarının toplamı
                                        (192) üstteki sipariş sayısıyla (174) aynı değil —
                                        iki farklı toplamı yan yana göstermek yanıltır. */}
                                    <div className={styles.donutCenter}>
                                        <span className={`${styles.donutTotal} num`}>{platforms.length}</span>
                                        <span className={styles.donutCaption}>platform</span>
                                    </div>
                                </div>
                                <ul className={styles.legend}>
                                    {platforms.map((p, i) => (
                                        <li key={p.platform} className={styles.legendItem}>
                                            <span
                                                className={styles.legendDot}
                                                style={{ background: PLATFORM_COLORS[i % PLATFORM_COLORS.length] }}
                                                aria-hidden="true"
                                            />
                                            <span className={styles.legendName}>{platformLabel(p.platform)}</span>
                                            <span className={`${styles.legendValue} num`}>{formatCount(p.total)}</span>
                                            <span className={styles.legendSub}>
                                                <span className="num">{formatCount(p.successful)}</span> başarılı
                                                {p.failed > 0 && (
                                                    <span className={styles.legendFail}>
                                                        {' · '}<span className="num">{formatCount(p.failed)}</span> hatalı
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                    </div>

                    {/* --- Zaman serisi --- */}
                    <section className={styles.block}>
                        <header className={styles.blockHeader}>
                            <h2 className={styles.blockTitle}>Zaman serisi</h2>
                            <div className={styles.bucketGroup} role="group" aria-label="Zaman aralığı">
                                {BUCKETS.map((b) => (
                                    <button
                                        key={b.value}
                                        type="button"
                                        className={`${styles.presetButton} ${bucket === b.value ? styles.presetActive : ''}`}
                                        aria-pressed={bucket === b.value}
                                        onClick={() => setBucket(b.value)}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                        </header>
                        <div className={styles.chartWrap}>
                            {(series?.points || []).length === 0 ? (
                                <p className={styles.muted}>Bu aralıkta veri yok.</p>
                            ) : (
                                <Bar data={chart} options={chartOptions} />
                            )}
                        </div>
                    </section>

                    {/* --- En çok satanlar --- */}
                    <div className={styles.twoCol}>
                        <section className={styles.block}>
                            <header className={styles.blockHeader}>
                                <h2 className={styles.blockTitle}>En çok satan planlar</h2>
                            </header>
                            {topPlans.length === 0 ? (
                                <p className={styles.muted}>Bu aralıkta satış yok.</p>
                            ) : (
                                <ol className={styles.rankList}>
                                    {topPlans.map((plan, i) => (
                                        <RankRow
                                            key={plan.planId}
                                            rank={i + 1}
                                            title={plan.planTitle}
                                            subtitle={<>{plan.countryOrRegion} · {formatTry(plan.estimatedRevenueTry)} tahmini ciro</>}
                                            value={formatCount(plan.orders)}
                                            share={(plan.orders / topPlanMax) * 100}
                                        />
                                    ))}
                                </ol>
                            )}
                        </section>

                        <section className={styles.block}>
                            <header className={styles.blockHeader}>
                                <h2 className={styles.blockTitle}>En çok satan ülkeler</h2>
                            </header>
                            {topCountries.length === 0 ? (
                                <p className={styles.muted}>Bu aralıkta satış yok.</p>
                            ) : (
                                <ol className={styles.rankList}>
                                    {topCountries.map((country, i) => (
                                        <RankRow
                                            key={country.bundleId}
                                            rank={i + 1}
                                            title={country.title}
                                            subtitle={<>{formatTry(country.estimatedRevenueTry)} tahmini ciro</>}
                                            value={formatCount(country.orders)}
                                            share={(country.orders / topCountryMax) * 100}
                                        />
                                    ))}
                                </ol>
                            )}
                        </section>
                    </div>

                    <OperationsPanel />

                    {/* Verinin bilinen boşlukları. Yanlış sayı göstermektense not düşmek;
                        ama ekranın yarısını kaplamasın diye katlanmış duruyor. */}
                    <details className={styles.limits}>
                        <summary>Bu sayıların sınırları</summary>
                        <ul>
                            <li>Mağaza cirosu tahmindir: sipariş anındaki fiyat saklanmıyor, planın güncel fiyatı kullanılıyor.</li>
                            <li>Aktivasyon verisi sağlayıcıdan güncellenmiyor; eSIM aktivasyon sayıları ölçüm değildir.</li>
                            <li>Kullanıcı doğrulaması (<code>isVerified</code>) güvenilir değil, bu yüzden gösterilmiyor.</li>
                            <li>Kupon kullanımı raporlanmıyor; iade/iptal edilen PayPal işlemleri ciroda görünmeye devam eder.</li>
                        </ul>
                    </details>
                </div>
            )}
        </div>
    );
};

export default StatsPage;
